/**
 * Phase 2b — orchestrator. Loads inputs, calls the pure scoring functions,
 * writes ONE idempotent row per (user_id, date) to `health_daily_scores`.
 *
 * Never throws to the caller: any failure is captured to analytics and
 * swallowed. Score compute must not fail the sync path.
 */

import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

import {
  BASELINE_WINDOW_DAYS,
  CALIBRATION_MIN_DAYS,
  CALIBRATION_READY_DAYS,
  STRAIN_CEILINGS_DEFAULT,
} from './scoringConfig';
import {
  computeRecovery,
  computeStrain,
  deriveRecoveryStage,
  median,
  type RecoveryResult,
  type StrainResult,
} from './scoring';
import { trackRecoveryComputed, trackScoringFailed, trackStrainComputed } from './analytics';

/** Local YYYY-MM-DD. */
const toLocalDateKey = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

interface MetricRow {
  date: string;
  sleep_minutes: number | null;
  resting_hr: number | null;
  hrv_sdnn: number | null;
  steps: number | null;
  active_energy_kcal: number | null;
}

interface WorkoutRow {
  exercises: unknown;
}

/** Sum weight×reps across COMPLETED sets only. Matches `get_muscle_focus`. */
const liftingVolumeFromWorkouts = (workouts: WorkoutRow[]): number => {
  let total = 0;
  for (const w of workouts) {
    const exercises = Array.isArray(w.exercises) ? (w.exercises as unknown[]) : [];
    for (const ex of exercises) {
      if (!ex || typeof ex !== 'object') continue;
      const sets = (ex as { sets?: unknown }).sets;
      if (!Array.isArray(sets)) continue;
      for (const s of sets) {
        if (!s || typeof s !== 'object') continue;
        const set = s as { weight?: unknown; reps?: unknown; completed?: unknown };
        if (set.completed !== true) continue;
        const w = typeof set.weight === 'number' ? set.weight : Number(set.weight ?? 0);
        const r = typeof set.reps === 'number' ? set.reps : Number(set.reps ?? 0);
        if (Number.isFinite(w) && Number.isFinite(r) && w > 0 && r > 0) total += w * r;
      }
    }
  }
  return total;
};

export interface RecomputeResult {
  recovery: RecoveryResult;
  strain: StrainResult;
}

/**
 * Recompute Recovery + Strain for `(userId, date)` and upsert one row.
 * Idempotent. Fire-and-forget from callers; returns null on failure.
 */
export const recomputeDailyScores = async (
  userId: string,
  date: Date = new Date(),
): Promise<RecomputeResult | null> => {
  try {
    const today = toLocalDateKey(date);

    // Window = today - BASELINE_WINDOW_DAYS ... today (baseline excludes today).
    const start = new Date(date);
    start.setDate(date.getDate() - BASELINE_WINDOW_DAYS);
    start.setHours(0, 0, 0, 0);

    const [metricsRes, workoutsRes] = await Promise.all([
      supabase
        .from('health_daily_metrics')
        .select('date,sleep_minutes,resting_hr,hrv_sdnn,steps,active_energy_kcal')
        .eq('user_id', userId)
        .gte('date', toLocalDateKey(start))
        .lte('date', today)
        .order('date', { ascending: true }),
      supabase
        .from('workouts')
        .select('exercises')
        .eq('user_id', userId)
        .eq('date', today),
    ]);

    if (metricsRes.error) throw metricsRes.error;
    if (workoutsRes.error) throw workoutsRes.error;

    const metrics = (metricsRes.data ?? []) as MetricRow[];
    const workouts = (workoutsRes.data ?? []) as WorkoutRow[];

    const todayRow = metrics.find((m) => m.date === today) ?? null;
    const baselineRows = metrics.filter((m) => m.date !== today);

    // Baseline: 14-day rolling median per metric, computed independently.
    const baseline = {
      sleepMinutes: median(baselineRows.map((r) => r.sleep_minutes)),
      hrvSdnn: median(baselineRows.map((r) => r.hrv_sdnn)),
      restingHr: median(baselineRows.map((r) => r.resting_hr)),
    };

    // Stage = min day-count across shared (non-zero) metrics.
    const dayCounts = {
      sleep: baselineRows.filter((r) => r.sleep_minutes != null).length,
      hrv: baselineRows.filter((r) => r.hrv_sdnn != null).length,
      rhr: baselineRows.filter((r) => r.resting_hr != null).length,
    };
    const stage = deriveRecoveryStage(dayCounts, {
      min: CALIBRATION_MIN_DAYS,
      ready: CALIBRATION_READY_DAYS,
    });

    const recovery = computeRecovery(
      {
        sleepMinutes: todayRow?.sleep_minutes ?? null,
        hrvSdnn: todayRow?.hrv_sdnn ?? null,
        restingHr: todayRow?.resting_hr ?? null,
      },
      baseline,
      stage,
    );

    const strain = computeStrain(
      {
        liftingVolumeKg: liftingVolumeFromWorkouts(workouts) || null,
        cardioKcal: todayRow?.active_energy_kcal ?? null,
        steps: todayRow?.steps ?? null,
      },
      STRAIN_CEILINGS_DEFAULT,
    );

    const { error: upsertErr } = await supabase.from('health_daily_scores').upsert(
      {
        user_id: userId,
        date: today,
        recovery_score: recovery.score,
        recovery_stage: recovery.stage,
        recovery_band: recovery.band,
        recovery_components: recovery.components as unknown as Json,
        recovery_signals_used: recovery.signalsUsed,
        strain_score: strain.score,
        strain_band: strain.band,
        strain_components: strain.components as unknown as Json,
        strain_signals_used: strain.signalsUsed,
        computed_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,date' },
    );
    if (upsertErr) throw upsertErr;

    trackRecoveryComputed(recovery.stage, recovery.band, recovery.signalsUsed);
    trackStrainComputed(strain.band, strain.signalsUsed);

    // Fire-and-forget: nudge the Health Score to pick up the fresh recovery.
    // Once per recompute. compute-health-score never writes health_daily_scores,
    // so this cannot recurse.
    void (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        await supabase.functions.invoke('compute-health-score', {
          body: { user_id: userId },
        });
      } catch {
        // non-fatal
      }
    })();

    return { recovery, strain };

  } catch (e) {
    trackScoringFailed(e instanceof Error ? e.message : 'unknown');
    return null;
  }
};
